import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import {
  Row,
  Col,
  CardBody,
  Button,
  Card,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";

import { Paginated } from "../../models";
import {
  State,
  PaginateQuery,
  OlympicQuestionDto,
  OlympicDto,
} from "../../interfaces";
import {
  deleteOlympicQuestion,
  deleteOlympicQuestionConsent,
  getOlympic,
  getQuestionsByOlympic,
  toggleAddEditOlympicQuestionModal,
} from "../../store/actions";
import { BreadCrumbItem, NoResultFound } from "../../components/Common";
import { Paginator, TableSpinner } from "../../components";
import DeleteModal from "../../components/Common/DeleteModal";
import smallImage13 from "../../assets/images/small/img-13.png";
import { AddEditOlympicQuestionsModal } from "./AddEditOlympicQuestionModal";

export const OlympicQuestionsPage = () => {
  //#region hooks
  const history = useHistory();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  //#endregion

  //#region internal state
  const { olympicId, courseId } = useParams<any>();
  const initState = new PaginateQuery(20);
  const [paginateQuery, setPaginateQuery] = useState<PaginateQuery>({
    ...initState,
    filter: {
      ["olympic"]: olympicId,
    },
  });
  const [showQuestionDeleteModal, setShowQuestionDeleteModal] =
    useState<boolean>(false);
  const [deletingQuestionId, setDeletingQuestionId] = useState<string | null>(
    null
  );
  const [showAddEditQuestionModal, setShowAddEditQuestionModal] =
    useState<boolean>(false);
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(
    null
  );
  const [questions, setQuestions] = useState<OlympicQuestionDto[]>([]);
  const [previousPages, setPreviousPages] = useState<BreadCrumbItem[]>([]);
  //#endregion

  //#region redux state
  const { loading: loadingQuestions, response: paginatedResponse } =
    useSelector(
      (state: any): State<Paginated<OlympicQuestionDto>> =>
        state.olympic.questionsByOlympic
    );

  const {
    loading: deleteQuestionLoading,
    success: deleteQuestionSuccess,
    error: deleteQuestionError,
  } = useSelector(
    (state: any): State<any> => state.olympic.deleteOlympicQuestion
  );

  const {
    response: olympic,
    loading: loadingOlympic,
    success: loadingOlympicSuccess,
    error: loadingOlympicError,
  } = useSelector((state: any): State<OlympicDto> => state.olympic.olympic);

  //#endregion

  //#region effects

  useEffect(() => {
    dispatch(getQuestionsByOlympic(paginateQuery));
  }, [paginateQuery]);

  useEffect(() => {
    if (olympicId) {
      dispatch(getOlympic(olympicId, history));
      dispatch(getQuestionsByOlympic(paginateQuery));
    }
  }, [olympicId]);

  useEffect(() => {
    if (!loadingOlympic && loadingOlympicError)
      toast.error(loadingOlympicError, {
        autoClose: 3000,
      });
  }, [loadingOlympic]);

  useEffect(() => {
    if (!loadingOlympic && loadingOlympicSuccess && olympic) {
      setPreviousPages([
        {
          title: i18n.language == "ar" ? olympic.name.ar : olympic.name.en,
          link: `/olympiad/${olympicId}`,
        },
      ]);
    }
  }, [loadingOlympic]);

  useEffect(() => {
    if (!deleteQuestionLoading && deleteQuestionSuccess) {
      setDeletingQuestionId(null);
      setShowQuestionDeleteModal(false);
      dispatch(getQuestionsByOlympic(paginateQuery));
    }
  }, [deleteQuestionLoading]);

  useEffect(() => {
    if (paginatedResponse && paginatedResponse.data) {
      setQuestions(paginatedResponse.data);
    }
  }, [paginatedResponse]);

  //#endregion

  //#region handlers

  const onAdd = () => {
    dispatch(toggleAddEditOlympicQuestionModal());
    setShowAddEditQuestionModal(true);
  };

  const onEdit = (id: string) => {
    setEditingQuestionId(id);
    setShowAddEditQuestionModal(true);
  };

  const onDelete = (id: string) => {
    setDeletingQuestionId(id);
    setShowQuestionDeleteModal(true);
    dispatch(deleteOlympicQuestionConsent(id));
  };

  const onDeleteConfirm = () => {
    dispatch(deleteOlympicQuestion(deletingQuestionId as string, history));
    setDeletingQuestionId(null);
  };

  const onChangePage = (page: number) => {
    console.log(page);
    setPaginateQuery({ ...paginateQuery, page: page });
  };

  //#endregion

  return (
    <React.Fragment>
      <Row className="mt-3">
        <Col>
          <Row>
            <Col className="d-flex justify-content-start">
              <h4 className="d-flex ms-1">
                <i className="ri-trophy-line me-1"></i>
                {i18n.language === "ar" ? olympic?.name.ar : olympic?.name.en}
              </h4>
            </Col>
            <Col className="d-flex justify-content-end">
              <Link
                color="success"
                onClick={onAdd}
                className="mx-1 btn"
                to={`/olympiad/${courseId}`}
              >
                {i18n.language === "ar" ? (
                  <i className=" ri-arrow-right-line me-1 align-bottom"></i>
                ) : (
                  <i className=" ri-arrow-left-line me-1 align-bottom"></i>
                )}
                {t("backToOlympic")}
              </Link>
              <Button color="success" onClick={onAdd}>
                <i className="ri-add-fill me-1 align-bottom"></i>
                {t("addItem", { item: "question" })}
              </Button>
            </Col>
          </Row>
        </Col>
        <ToastContainer />
        <Col xxl={12}>
          <Row className="team-list grid-view-filter mt-2">
            {!loadingQuestions &&
              questions &&
              questions.length > 0 &&
              questions.map((item: OlympicQuestionDto, key) => (
                <Col key={key}>
                  <Card className="team-box">
                    <img
                      src={item.displayImage?.path || smallImage13}
                      alt=""
                      style={{ objectFit: "cover", height: 250 }}
                    />
                    <CardBody>
                      <UncontrolledDropdown
                        style={
                          i18n.language === "ar"
                            ? {
                                position: "absolute",
                                top: 10,
                                left: 15,
                              }
                            : {
                                position: "absolute",
                                top: 10,
                                right: 15,
                              }
                        }
                      >
                        <DropdownToggle
                          tag="a"
                          id="dropdownMenuLink2"
                          role="button"
                        >
                          <i className="ri-more-fill fs-17"></i>
                        </DropdownToggle>
                        <DropdownMenu>
                          <DropdownItem
                            style={{
                              alignSelf: "flex-start",
                              display: "flex",
                            }}
                            className="dropdown-item edit-list"
                            onClick={() => onEdit(item.id)}
                          >
                            <i className="ri-pencil-line me-2 align-bottom text-muted"></i>
                            {t("edit")}
                          </DropdownItem>
                          <DropdownItem
                            style={{
                              alignSelf: "flex-start",
                              display: "flex",
                            }}
                            className="dropdown-item remove-list"
                            onClick={() => onDelete(item.id)}
                          >
                            <i className="ri-delete-bin-5-line me-2 align-bottom text-muted"></i>
                            {t("delete")}
                          </DropdownItem>
                        </DropdownMenu>
                      </UncontrolledDropdown>

                      <Row>
                        <Col sm={12}>
                          <div
                            style={{ maxHeight: "40px" }}
                            className="text-primary"
                            dangerouslySetInnerHTML={{
                              __html:
                                i18n.language === "ar"
                                  ? item.content.ar
                                  : item.content.en,
                            }}
                          ></div>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              ))}
          </Row>
          {!loadingQuestions && questions && questions.length > 0 && (
            <Paginator
              itemsOnCurrentPage={questions?.length || 0}
              itemsPerPage={paginatedResponse?.meta?.itemsPerPage as number}
              totalItems={paginatedResponse?.meta?.totalItems || 0}
              currentPage={paginatedResponse?.meta?.currentPage || 1}
              totalPages={paginatedResponse?.meta?.totalPages || 0}
              onChangePage={(pageToChange: number) =>
                onChangePage(pageToChange)
              }
            />
          )}
          {loadingQuestions && <TableSpinner />}
          {!loadingQuestions && (!questions || questions.length == 0) && (
            <NoResultFound />
          )}
        </Col>
      </Row>
      <DeleteModal
        loading={deleteQuestionLoading}
        show={showQuestionDeleteModal}
        onDeleteClick={() => onDeleteConfirm()}
        onCloseClick={() => {
          setShowQuestionDeleteModal(false);
        }}
        error={deleteQuestionError}
      />
      <AddEditOlympicQuestionsModal
        olympicId={olympicId}
        id={editingQuestionId}
        show={showAddEditQuestionModal}
        onCloseClick={() => {
          dispatch(toggleAddEditOlympicQuestionModal());
          setShowAddEditQuestionModal(false);
          setEditingQuestionId(null);
          dispatch(getQuestionsByOlympic(paginateQuery));
        }}
      />
    </React.Fragment>
  );
};
